import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Card, Checkbox, Divider, Text } from '@factorialco/deprecated-design-system'
import {
  DuplicateTrainingDocument,
  DuplicateTrainingsModalDocument,
  TrainingsDataCollectionDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Loading from 'modules/layout/components/Page/Loading'

export type DuplicateTrainingForm = {
  name: string
  basicInfo: boolean
  groupMaterials: boolean
  sessions: boolean
  participants: boolean
  trainingMaterials: boolean
  surveys: boolean
}
const DuplicateTraining = () => {
  const [duplicateTraining, { loading }] = useMutation(DuplicateTrainingDocument, {
    extractorFn: (data) => data.trainingsMutations.duplicateTrainingsTraining,
    awaitRefetchQueries: true,
    refetchQueries: [TrainingsDataCollectionDocument],
  })

  const { id } = useParams()
  const { data, loading: loadingTraining } = useQuery(DuplicateTrainingsModalDocument, {
    variables: { id: String(id) },
  })
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const back = () => navigate(navigation.trainings.list.index.fullPath())
  const { values, setValues, handleSubmit, isValid } = useForm<DuplicateTrainingForm>({
    defaultValues: {
      surveys: false,
      trainingMaterials: false,
      participants: false,
      sessions: false,
      groupMaterials: false,
      basicInfo: true,
      name: `${data?.trainings.training?.name} (Copy)`,
    },
    validate: ({ basicInfo, trainingMaterials, groupMaterials, participants, sessions, name }) => {
      const errors: FormErrors<DuplicateTrainingForm> = {}
      if (
        (!basicInfo &&
          !trainingMaterials &&
          !groupMaterials &&
          !participants &&
          !sessions &&
          !name) ||
        !name
      ) {
        errors.name = 'Mandatory'
      }
      return errors
    },
    onSubmit: async (data) => {
      const response = await duplicateTraining({
        variables: {
          id: String(id),
          name: data.name,
          basicInfo: data.basicInfo,
          sessions: data.sessions,
          classFiles: data.groupMaterials,
          employees: data.participants,
          trainingFiles: data.trainingMaterials,
          surveys: data.surveys,
        },
      })
      const handled = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.duplicate_training.error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.duplicate_training.success'),
        })
        back()
      }

      return handled
    },
  })

  const classesChecked = values.sessions || values.participants || values.groupMaterials
  const indeterminate =
    (values.sessions || values.participants || values.groupMaterials) &&
    !(values.sessions && values.participants && values.groupMaterials)

  const onClassesChecked = () => {
    if (indeterminate) {
      setValues({
        ...values,
        sessions: true,
        participants: true,
        groupMaterials: true,
      })
    } else {
      setValues({
        ...values,
        sessions: !values.sessions,
        participants: !values.participants,
        groupMaterials: !values.groupMaterials,
      })
    }
  }

  return (
    <Modal
      title={i18n.t('trainings.duplicate_training.title')}
      description={i18n.t('trainings.duplicate_training.description')}
      onSubmit={() => {
        track(trackingEvents.training.duplicate.saveDuplicateCourse, {
          course_overview: values.basicInfo,
          course_groups: classesChecked,
          course_materials: values.trainingMaterials,
          course_surveys: values.surveys,
        })
        handleSubmit()
      }}
      onClose={back}
      illustration="document-edit"
      footer={
        <>
          <Button.Router to={navigation.trainings.list.index.fullPath()}>
            {i18n.t('common.cancel')}
          </Button.Router>
          <Button submit disabled={!isValid} loading={loading}>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      {loading || loadingTraining ? (
        <Loading />
      ) : (
        <Box gap="s24">
          <TextField
            value={values.name}
            label={'Course name'}
            mandatory
            onChange={(value) => {
              setValues({ ...values, name: value })
            }}
          />
          <Divider />
          <Box gap="s16">
            <Card>
              <Card.Title
                size="small"
                action={
                  <Checkbox
                    size="medium"
                    checked={values.basicInfo}
                    onChange={() => setValues({ ...values, basicInfo: !values.basicInfo })}
                  />
                }
              >
                {i18n.t('trainings.duplicate_training.params.basic_info')}
              </Card.Title>
            </Card>
            <Card>
              <Card.Title
                size="small"
                action={
                  <Checkbox
                    size="medium"
                    checked={classesChecked}
                    indeterminate={indeterminate}
                    onChange={() => onClassesChecked()}
                  />
                }
              >
                {i18n.t('trainings.duplicate_training.params.classes')}
              </Card.Title>
              <Card.ExpandableContent expanded={classesChecked} onExpandChange={onClassesChecked}>
                <Box gap="s16">
                  <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
                    <Box>
                      <Text size="300" weight="normal">
                        {i18n.t('trainings.duplicate_training.params.events')}
                      </Text>
                    </Box>
                    <Checkbox
                      size="medium"
                      checked={values.sessions}
                      disabled={classesChecked}
                      onChange={() => setValues({ ...values, sessions: !values.sessions })}
                    />
                  </Box>
                  <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
                    <Box>
                      <Text size="300" weight="normal">
                        {i18n.t('trainings.duplicate_training.params.assigned_employees')}
                      </Text>
                    </Box>
                    <Checkbox
                      size="medium"
                      checked={values.participants}
                      onChange={() => setValues({ ...values, participants: !values.participants })}
                    />
                  </Box>
                  <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
                    <Box>
                      <Text size="300" weight="normal">
                        {i18n.t('trainings.duplicate_training.params.attachments')}
                      </Text>
                    </Box>
                    <Checkbox
                      size="medium"
                      checked={values.groupMaterials}
                      onChange={() =>
                        setValues({ ...values, groupMaterials: !values.groupMaterials })
                      }
                    />
                  </Box>
                </Box>
              </Card.ExpandableContent>
            </Card>
            <Card>
              <Card.Title
                size="small"
                action={
                  <Checkbox
                    size="medium"
                    checked={values.trainingMaterials}
                    onChange={() =>
                      setValues({ ...values, trainingMaterials: !values.trainingMaterials })
                    }
                  />
                }
              >
                {i18n.t('trainings.duplicate_training.params.training_docs')}
              </Card.Title>
            </Card>
            <Card>
              <Card.Title
                size="small"
                action={
                  <Checkbox
                    size="medium"
                    checked={values.surveys}
                    onChange={() => setValues({ ...values, surveys: !values.surveys })}
                  />
                }
              >
                {i18n.t('trainings.duplicate_training.params.surveys')}
              </Card.Title>
            </Card>
          </Box>
        </Box>
      )}
    </Modal>
  )
}

export default DuplicateTraining
